import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import {
    DAAS_COLLECTION_METADATA_CACHE,
    getQueryString,
    type UseDaasGetCollectionMetadataProps,
    useQueryDisabled,
} from '@pkg/hooks';
import { useGetSlugChain } from '@pkg/hooks/tokens/useGetSlugChain';
import { type MultichainCollectionMetadataProps } from '@pkg/hooks/types/daas';

import {
    type ApiResult,
    type AptosCollectionMetadata,
    type ChainCollectionMetadata,
    type DaasMarketplaceCollection,
} from '@app/client';
import { callGetCollectionMetadata } from '@app/client/resources/daas';
import { PathParams } from '@app/routes/types';

const getQueryKey = (slugQuery: string) => [DAAS_COLLECTION_METADATA_CACHE, slugQuery];
export function useGetMultichainCollectionMetadata({
    slugQuery = [],
    disabled = false,
    type = 'collection',
}: MultichainCollectionMetadataProps) {
    const queryClient = useQueryClient();
    const customQueryString = getQueryString({
        queryArrayKey: type === 'contract' ? 'addresses' : 'slugs',
        queryArray: slugQuery.sort(),
        queryParams: type === 'contract' ? { include_slugs: true } : undefined,
    });
    const queryKey = getQueryKey(customQueryString);

    const { data, ...rest } = useQuery({
        queryKey,
        queryFn: () =>
            callGetCollectionMetadata({
                type,
                customQuery: customQueryString,
            }),
        enabled: !!slugQuery?.length,
        retry: false,
        placeholderData: keepPreviousData,
        select: (res) => {
            const data = res as ApiResult<ChainCollectionMetadata>;
            if (data?.ok && data.value) {
                return data?.value;
            }
        },
    });

    const getCollectionMetadata = ({
        slugQuery = [],
        ignoreCancel,
    }: MultichainCollectionMetadataProps): Promise<ApiResult<ChainCollectionMetadata>> | undefined => {
        const customQueryString = getQueryString({ queryArrayKey: 'slugs', queryArray: slugQuery?.sort() });
        if (disabled || !slugQuery?.length) return;

        const queryKey = getQueryKey(customQueryString);
        return queryClient.fetchQuery({
            queryKey,
            queryFn: ({ signal }) =>
                callGetCollectionMetadata({
                    type: 'collection',
                    settings: { signal: ignoreCancel ? undefined : signal },
                    customQuery: customQueryString,
                }) as Promise<ApiResult<ChainCollectionMetadata>>,
        }) as Promise<ApiResult<ChainCollectionMetadata>>;
    };

    return { getCollectionMetadata, data, ...rest };
}

export function useGetSingleCollectionMetadata(props?: UseDaasGetCollectionMetadataProps) {
    const { includeAttributes, slug, disabled, chain, limit = 50 } = props || {};
    const { [PathParams.CollectionSlug]: collectionSlugParam } = useParams();
    const querySlug = slug || collectionSlugParam;
    const _chain = useGetSlugChain({ type: 'daas' });
    const queryChain = chain || _chain;

    const querySlugString = `${queryChain}.${querySlug}`;
    const query = {
        slugs: querySlugString,
        include_attributes: Boolean(includeAttributes),
        limit: limit,
    };

    const customQueryString = getQueryString({ queryParams: query });

    const queryDisabled = useQueryDisabled([!disabled, querySlug?.length, queryChain]);

    const queryKey = getQueryKey(customQueryString);
    const { data, ...rest } = useQuery({
        queryKey,
        queryFn: () =>
            callGetCollectionMetadata({
                type: 'collection',
                customQuery: customQueryString,
            }),
        retry: false,
        select: (res) => {
            type Res = AptosCollectionMetadata | DaasMarketplaceCollection;
            const data = res as ApiResult<
                ChainCollectionMetadata<
                    Res extends AptosCollectionMetadata ? AptosCollectionMetadata : DaasMarketplaceCollection
                >
            >;
            if (data?.ok && data.value && queryChain) {
                return data?.value?.[queryChain]?.[0] as DaasMarketplaceCollection;
            }
        },
        enabled: !queryDisabled && !!querySlug?.length,
    });

    return { data, chain: queryChain, ...rest };
}
